import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  ContactsFilled,
  FileExcelOutlined,
  FileTextOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SlidersOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Empty,
  Image,
  Table,
  Typography,
} from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory } from "react-router-dom";
import default_bank from "../../../../Assets/ic_default_bank.png";
import GetConsentByHandleResponse from "../../../../Models/Response/Consent/GetConsentByHandle";
import {
  Accounts,
  GetConsentByIdResponse,
} from "../../../../Models/Response/Consent/GetConsentById";
import { logoBaseUrl } from "../../../../Utils/Constants/baseUrl";
import { Routes } from "../../../../Utils/Constants/routes";
import TimeFromNow from "../../../Common/Components/TimeFromNow";

import "./index.css";
import LinkedDelinkedAccountResponse from "../../../../Models/Response/Account/LinkedDelinkedAccounts";

interface IConsentDetailsProps {
  createdOrUpdatedOn: string;
  type: string;
  consentByUniqueIdDetails: GetConsentByIdResponse | GetConsentByHandleResponse;
  account?: Accounts[];
}

const columns = [
  {
    title: "type",
    dataIndex: "type",
  },
  {
    title: "operator",
    dataIndex: "operator",
  },
  {
    title: "value",
    dataIndex: "value",
  },
];
const ConsentDetails: React.FC<IConsentDetailsProps> = ({
  createdOrUpdatedOn,
  type,
  consentByUniqueIdDetails,
  account,
  children,
}) => {
  const { push } = useHistory();
  const { Text, Title } = Typography;
  const consentRedirect = () => push(Routes.consent);
  const { t } = useTranslation();

  return (
    <>
      <div className="detailsHeader">
        <ArrowLeftOutlined className="backIcon" onClick={consentRedirect} />
        <Image
          className="fipLogo"
          src={logoBaseUrl + consentByUniqueIdDetails.DataConsumer.id + ".png"}
          fallback={default_bank}
          preview={false}
        />
        <div>
          <Title level={5}>{consentByUniqueIdDetails.DataConsumer.id}</Title>
          <Text>{t("wants to access your linked account")}</Text>
        </div>
        <TimeFromNow
          date={createdOrUpdatedOn}
          title={type === "PENDING" ? t("Created") : t("Updated")}
        />
      </div>
      <div className="consentDetailsBody">
        <Text className="typoText">
          {t("Account permission and information access")}
        </Text>
        <div className="detailsType">
          <ContactsFilled className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("Consent Type")}</Text>
            <Text className="detailsValue">
              {consentByUniqueIdDetails.consentTypes.map(
                (types: any, index: any) => {
                  let isComma;
                  index === consentByUniqueIdDetails.consentTypes.length - 1
                    ? (isComma = "")
                    : (isComma = ", ");

                  return types + isComma;
                }
              )}
            </Text>
          </div>
        </div>

        <div className="detailsType">
          <ClockCircleOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">
              {t("FI Data Pull Frequency")}
              {consentByUniqueIdDetails.fetchType !== "ONETIME"
                ? ` (${consentByUniqueIdDetails.fetchType})`
                : "(PERIODIC)"}
            </Text>

            <Text className="detailsValue">
              {consentByUniqueIdDetails.fetchType === "ONETIME"
                ? consentByUniqueIdDetails.fetchType
                : `${t("Information will be fetched")} ${
                    consentByUniqueIdDetails.Frequency.value
                  } ${t("times a")} ${
                    consentByUniqueIdDetails.Frequency.unit
                  }/s`}
            </Text>
          </div>
        </div>
        <div className="detailsType">
          <FileExcelOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("CONSENT EXPIRY")}</Text>
            <Text className="detailsValue">
              {new Date(consentByUniqueIdDetails.consentExpiry)
                .toDateString()
                .slice(4)}
            </Text>
          </div>
        </div>
        <div className="detailsType">
          <FileTextOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("Purpose")}</Text>
            <Text className="detailsValue">
              {consentByUniqueIdDetails.Purpose.text}
            </Text>
          </div>
        </div>
        <div className="detailsType">
          <ContactsFilled className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("Consumer ID")}</Text>

            <Text className="detailsValue">
              {consentByUniqueIdDetails.DataConsumer.id}
            </Text>
          </div>
        </div>

        <div className="detailsType">
          <SlidersOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("Consent Mode")}</Text>

            <Text className="detailsValue">
              {consentByUniqueIdDetails.consentMode}
            </Text>
          </div>
        </div>

        <div className="detailsType">
          <IdcardOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("Customer Id")}</Text>

            <Text className="detailsValue">
              {consentByUniqueIdDetails.Customer.id}
            </Text>
          </div>
        </div>
        <div className="detailsType">
          <Collapse>
            <Collapse.Panel key={1} header={"Data Filters"}>
              <Table
                columns={columns}
                dataSource={consentByUniqueIdDetails.DataFilter}
                pagination={false}
              />
            </Collapse.Panel>
          </Collapse>
        </div>
        <div className="detailsType">
          <InfoCircleOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("FIType/s")}</Text>
            <Text className="detailsValue">
              {consentByUniqueIdDetails.fiTypes.map(
                (types: any, index: any) => {
                  let isComma;
                  index === consentByUniqueIdDetails.fiTypes.length - 1
                    ? (isComma = "")
                    : (isComma = ", ");

                  return types + isComma;
                }
              )}
            </Text>
          </div>
        </div>
        <div className="detailsType">
          <ClockCircleOutlined className="detailsIcon" />

          <div className="detailsDescription">
            <Text className="detailsTitle">{t("FI Data Range")}</Text>
            <Text className="detailsValue">
              {t("From")}{" "}
              {new Date(consentByUniqueIdDetails.FIDataRange.from)
                .toDateString()
                .slice(4)}{" "}
              {t("to")}{" "}
              {new Date(consentByUniqueIdDetails.FIDataRange.to)
                .toDateString()
                .slice(4)}
            </Text>
          </div>
        </div>
        <div className="detailsType">
          <ClockCircleOutlined className="detailsIcon" />
          <div className="detailsDescription">
            <Text className="detailsTitle">{t("FI Data Life")}</Text>

            <Text className="detailsValue">
              {`FIU will store data for ${consentByUniqueIdDetails.DataLife.value} ${consentByUniqueIdDetails.DataLife.unit}(s)`}
            </Text>
          </div>
        </div>
        <div className="detailsType"></div>

        {account && account.length && (
          <div className="consentDetailsAccounts">
            <Text className="typoText">{t("Linked Accounts")}</Text>
            {account.map((accounts: any, index: any) => (
              <Card
                key={index}
                className="accountCard activeAccountCard"
                id={index.toString()}
              >
                <Image
                  className="fipLogo"
                  src={logoBaseUrl + accounts.fipId + ".png"}
                  fallback={default_bank}
                  preview={false}
                />

                <div className="accountInfo">
                  <Text className="type">FipName : </Text>
                  <Text className="value" type="secondary">
                    {accounts.fipName}
                  </Text>
                  <br />
                  {accounts.fiType ? (
                    <>
                      <Text className="type">FIType : </Text>
                      <Text className="value" type="secondary">
                        {accounts.fiType}
                      </Text>
                      <br />
                    </>
                  ) : null}
                  <Text className="type">MaskedAccNumber : </Text>
                  <Text className="value" type="secondary">
                    {accounts.maskedAccNumber}
                  </Text>
                </div>

                <Checkbox checked={true} className="accountsCardCheckBox" />
              </Card>
            ))}
          </div>
        )}

        {children}
      </div>
    </>
  );
};

export default ConsentDetails;
